import { useQuery } from '@apollo/react-hooks';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Box } from 'localmed-core';
import React, { ComponentProps, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Theme } from '../../../../../diTheme';
import {
  COUNTRY_NAMES,
  COUNTRY_NAME_ADJECTIVE,
} from '../../../../../shared/components/AppointmentForm/TermsAgreementMessage';
import { DiButton, Link } from '../../../../../shared/components/DiComponents';
import SchedulingDetailsCard from '../../../../components/SchedulingDetailsCard';
import { COUNTRY_CODES } from '../../../constants';
import BackLink from '../BackLink';
import { IStepProps } from '../schedulingWizard';
import { BackButton, CheckBox, ContentWrapper, Wrapper } from '../styles';
import SchedulingInsuranceSelection from './components/SchedulingInsuranceSelection';
import SchedulingProviderSelection from './components/SchedulingProviderSelection';
import query from './SchedulingProviderQuery.graphql';

interface Props extends IStepProps {}

export const Avatar = styled('img')`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
`;

export const AvatarContainer = styled('div')<ComponentProps<'div'>, Theme>(
  ({ theme }) => css`
    min-width: ${theme.space.xxl};
    display: flex;
    justify-content: center;
    margin-right: ${theme.space.sm};
  `
);

export const List = styled('ul')(
  css`
    display: flex;
    align-items: center;
    position: relative;
    padding: 0;
    margin: 0;
  `
);

export const CardRowLeft = styled(Box)(
  css`
    display: flex;
    align-items: center;
  `
);

export const CardRow = styled(Box)(
  css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `
);

export const StyledLink = styled(Link)<ComponentProps<typeof Link>, Theme>(
  ({ theme }) => css`
    display: block;
    height: 0;
    overflow: hidden;
    margin: 0 2px;

    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      height: 8px;
      width: 8px;
      border-radius: 100%;
      background-color: ${theme.colors.black};
    }
  `
);

const Label = styled('label')<ComponentProps<'label'>, Theme>(
  ({ theme }) => css`
    display: flex;
    align-items: baseline;
    margin-top: ${theme.space.md};
    margin-bottom: ${theme.space.md};
  `
);

export default function SchedulingDetailsStep({ wizard }: Props) {
  const [openProviderSelection, setOpenProviderSelection] = useState<boolean>(false);
  const [openPaymentSelection, setOpenPaymentSelection] = useState<boolean>(false);

  const { provider } = wizard?.values || {};
  const { data } = useQuery(query, {
    variables: { id: provider?.id },
    skip: !provider?.id || (!!provider?.display && !!provider?.profile),
  });

  let fullProvider = provider;
  if (data?.provider) {
    fullProvider = data.provider;
  }

  useEffect(() => {
    if (wizard) {
      wizard.form.change('provider', fullProvider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullProvider]);

  useEffect(() => {
    if (wizard?.trackSchedulingEvent) {
      wizard.trackSchedulingEvent(
        'Appointment Details Confirmation Screen',
        'SchedulingDetailsStep'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!wizard) return null;

  const {
    office,
    termAgreedAt,
    patientGivenName,
    patientFamilyName,
    contactGivenName,
    contactFamilyName,
    bookingForAnother,
    showEditInfo,
  } = wizard.values;

  if (openProviderSelection) {
    return <SchedulingProviderSelection wizard={wizard} setIsOpen={setOpenProviderSelection} />;
  }
  if (openPaymentSelection) {
    return <SchedulingInsuranceSelection wizard={wizard} setIsOpen={setOpenPaymentSelection} />;
  }
  const TOSLink = (
    <Link to="/patient-terms" target="_blank" fullNavigation>
      <FormattedMessage
        id="scheduling.SchedulingDetailsStep.termsOfServiceLinkMessage"
        defaultMessage="Terms of Use"
      />
    </Link>
  );
  const practiceName = office.practice?.display || 'LocalMed';
  const tosMessage =
    office.countryCode === COUNTRY_CODES.us ? (
      <span>
        <FormattedMessage
          id="scheduling.SchedulingDetailsStep.termsOfServiceMessage"
          defaultMessage="I agree to the LocalMed {TOSLink} and to receive electronic communications (including text messages) from {practiceName}."
          values={{
            TOSLink,
            practiceName,
          }}
        />
      </span>
    ) : (
      <span>
        <FormattedMessage
          id="scheduling.SchedulingDetailsStep.termsOfServiceMessage"
          defaultMessage="I agree to the LocalMed {TOSLink} and to receive electronic communications (including text messages) from {practiceName}. I
          understand by booking this appointment my information will be stored outside of {countryName} in a
          manner that is compliant with {countryAdjective} and US health information protection laws. If you
          have any questions or concerns please call {officePhone}."
          values={{
            TOSLink,
            countryName: COUNTRY_NAMES[office.countryCode] || office.countryCode,
            countryAdjective: COUNTRY_NAME_ADJECTIVE[office.countryCode] || office.countryCode,
            officePhone: office.phone,
            practiceName,
          }}
        />
      </span>
    );

  return (
    <Wrapper textAlign="left">
      <BackLink
        onClick={() => {
          wizard.gotoPreviousStep();
        }}
      />
      <ContentWrapper>
        <Box width="100%">
          <SchedulingDetailsCard
            setOpenPaymentSelection={setOpenPaymentSelection}
            setOpenProviderSelection={setOpenProviderSelection}
            showEditInfo={showEditInfo}
            goBack={() => {
              wizard.gotoPreviousStep();
            }}
            goToTimeSelectionStep={() => {
              wizard.gotoStep('SCHEDULING_TIME_SELECTION_STEP');
            }}
            {...wizard.values}
            patientName={`${patientGivenName || ''} ${patientFamilyName || ''}`}
            contactName={
              bookingForAnother ? `${contactGivenName || ''} ${contactFamilyName || ''}` : ''
            }
          />
          <Box fontSize="sm">
            <Label htmlFor="tos">
              <CheckBox
                type="checkbox"
                id="tos"
                name="tos"
                checked={!!termAgreedAt}
                onChange={(event) => {
                  wizard.form.batch(() => {
                    wizard.form.change('termAgreedAt', event.target.checked ? new Date() : null);
                    wizard.form.change('termsAgreement', event.target.checked);
                  });
                }}
              />
              {tosMessage}
            </Label>
          </Box>
        </Box>
      </ContentWrapper>
      <Box display="flex" justifyContent={{ _: 'center', lg: 'space-between' }} my="md">
        <BackButton
          {...wizard.previousButtonProps}
          display={{ _: 'none', lg: 'inherit' }}
          variant="subtle"
          size="xl"
        >
          <FormattedMessage
            id="scheduling.SchedulingDetailsStep.backButton"
            defaultMessage="Back"
          />
        </BackButton>
        <DiButton {...wizard.nextButtonProps} size="xl">
          <FormattedMessage
            id="scheduling.SchedulingDetailsStep.scheduleAppointmentButton"
            defaultMessage="Book Appointment"
          />
        </DiButton>
      </Box>
    </Wrapper>
  );
}

SchedulingDetailsStep.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  id: 'SCHEDULING_DETAILS_STEP',
};
